import { useCallback, useEffect, useRef } from 'react';
import {
  useQuizMetaContext,
  useQuizResults,
  useFollowerQuizGameState,
} from '@mentimeter/quiz';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import type { QuizOpenStaticContent } from '@core-api/presentation/types/response';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { GameStatesSwitcher } from './GameStateSwitcher';
import { useQuizOpenPreviewResultsLegacy } from './hooks/use-quiz-open-preview-results-legacy';

export const FollowerPresentationDataHandler = ({
  activeSession,
}: {
  activeSession: number;
}) => {
  const { useQuestion, isPreviewResultsEnabled } = usePresentationContext();
  const slide = useQuestion();

  const staticContent = slide.staticContent as QuizOpenStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const { totalCount, currentIndex, follower } = useQuizMetaContext();
  const { data = [] } = useQuizResults<QuizPlayersScoreData>();
  const {
    gameState,
    timeOffset = 0,
    connectedPlayers,
    respondents,
    joinAsFollower,
    connectedWithDelay,
  } = useFollowerQuizGameState();

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      joinAsFollower();
    }
  }, [joinAsFollower, follower, activeSession, gameState]);

  const onReload = useCallback(() => {
    window.location.reload();
  }, []);

  const firstCorrectAnswer = interactiveContent.choices[0]?.title;
  const shouldShowLegacyImage =
    staticContent.image.imageLayoutPosition.start === 0 &&
    staticContent.image.imageLayoutPosition.end === 0;

  const previewResults = useQuizOpenPreviewResultsLegacy({
    isPreviewResultsEnabled,
    correctAnswer: firstCorrectAnswer,
  });

  const resultsToShow = isPreviewResultsEnabled ? previewResults : data;

  return (
    <GameStatesSwitcher
      isReadonly={true}
      onMarkAnswer={undefined}
      everyoneHasVoted={false}
      visibleCorrectAnswer={firstCorrectAnswer ?? ''}
      imageUrl={
        slide.image?.presets?.original?.url ?? slide.image?.presets?.large?.url
      }
      timeBasedScoring={interactiveContent.scoring === 'time_based'}
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      description={interactiveContent.description}
      textSizeOffset={staticContent.textSizeOffset}
      results={resultsToShow}
      isPreviewResults={isPreviewResultsEnabled}
      gameState={gameState}
      timeOffset={timeOffset}
      connectedPlayers={connectedPlayers}
      respondents={respondents}
      onReload={onReload}
      connectedWithDelay={connectedWithDelay}
      totalQuizQuestions={totalCount}
      currentQuizQuestionIndex={currentIndex}
      shouldShowLegacyImage={shouldShowLegacyImage}
    />
  );
};
